/**
 * useCmsCustomPage — fetch a custom page by slug, seeded from the build-time
 * snapshot (`cmsCustomPageBySlug`) so prerendered HTML is complete.
 *
 * Mirrors `useCmsFetch` but keys the snapshot lookup by the slug param, which
 * is the route-local locale that `parseLocaleFromPath` already resolved.
 * `useCmsFetch` already sends `?locale=<i18n.language>` + `previewTokenParam()`,
 * and i18n.language matches the path locale at prerender time
 * (entry-server.tsx sets it first), so it can be used directly — no fork.
 *
 * The snapshot holds ALL THREE locales (per-locale columns + a per-locale `seo`
 * map), so it must be narrowed to the CURRENT locale before it can stand in
 * for the wire payload — otherwise an /en/ visitor is served the Traditional
 * text during the first paint.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'
import { cmsCustomPageBySlug } from '@/content/cms/custom-pages.generated'
import { customPageEntryToWire, type CmsCustomPageWire } from '@/content/cms/customPageWire'

/** The snapshot row for one slug, narrowed to one locale. Null when the slug
 *  is not in the snapshot (a draft, or a page published after the build). */
function seedWire(slug: string, locale: string): CmsCustomPageWire | null {
  const entry = cmsCustomPageBySlug[slug] ?? null
  return entry ? customPageEntryToWire(entry, locale) : null
}

interface CustomPageState {
  /** `${slug}|${locale}` — the request this state belongs to. */
  key: string
  data: CmsCustomPageWire | null
  /** Whether the request has come back. Without it a missing page would sit
   *  in the loading state forever, because `data === null` is also the
   *  "not fetched yet" value. */
  settled: boolean
}

export function useCmsCustomPage(slug?: string) {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  const { slug: paramSlug } = useParams<{ slug: string }>()
  const targetSlug = slug ?? paramSlug ?? ''
  const key = `${targetSlug}|${locale}`

  const [state, setState] = useState<CustomPageState>(() => ({
    key,
    data: seedWire(targetSlug, locale),
    settled: false,
  }))

  useEffect(() => {
    if (!targetSlug) return
    const controller = new AbortController()
    let cancelled = false

    // Re-seed on every slug/locale change: the snapshot is per-slug, and the
    // seed must match the locale being rendered.
    setState({ key, data: seedWire(targetSlug, locale), settled: false })

    void fetch(
      `/api/cms/public/custom-pages/${encodeURIComponent(targetSlug)}?locale=${encodeURIComponent(locale)}${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<unknown>) : null))
      .then((raw) => {
        if (cancelled) return
        const next = (raw as { custom_page?: CmsCustomPageWire } | null)?.custom_page ?? null
        setState({
          key,
          data: next ?? seedWire(targetSlug, locale),
          settled: true,
        })
      })
      .catch(() => {
        // Network error / abort — keep the snapshot seed, but stop the spinner.
        if (!cancelled) setState((s) => ({ ...s, settled: true }))
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [targetSlug, locale, key])

  // The state belongs to the request that produced it; a slug or locale
  // change must never render the previous page's content for a frame.
  const active = state.key === key ? state : { key, data: seedWire(targetSlug, locale), settled: false }

  return {
    data: active.data,
    loading: active.data === null && !active.settled,
    error: active.data === null && active.settled ? ('not_found' as const) : null,
  }
}
